import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { createCode } from "supertokens-web-js/recipe/passwordless";
import Session from "supertokens-web-js/recipe/session";

import { AuthenticationPage } from "@/components/AuthenticationPage/AuthenticationPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { AlertCircleIcon } from "@/components/Icons/AlertCircleIcon";
import { Hourglass01Icon } from "@/components/Icons/Hourglass01Icon";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { DEFAULT_APP_BRANDING } from "@/helpers/app-branding";
import { removeFromHeaders } from "@/helpers/headers";
import { useBool } from "@/hooks/useBool";
import { useInterval } from "@/hooks/useInterval";
import { useIsWindowFocused } from "@/hooks/useIsWindowFocused";
import { useQueryParam } from "@/hooks/useQueryParam";
import { usePublicAppBranding } from "@/providers/PublicAppBrandingProvider";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

const RESEND_COOLDOWN_SECONDS = 60;
const SESSION_POLL_INTERVAL_MS = 30_000;

export function MagicLinkSentPage(): React.ReactNode {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const showFlashToast = useFlashToast();
  const { appBranding } = usePublicAppBranding();

  const [email] = useQueryParam("email");
  const [isResending, resendingHandlers] = useBool();
  const [resendError, setResendError] = useState<string | undefined>();
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  const navigateToRegistration = useCallback(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const registrationCode = queryParams.get("code");
    const type = queryParams.get("type");

    const newQueryParams = new URLSearchParams();
    if (registrationCode) {
      newQueryParams.set("code", registrationCode);
    }

    if (type) {
      newQueryParams.set("type", type);
    }

    void navigate(`${routes.registration.main()}?${newQueryParams.toString()}`);
  }, [navigate]);

  const isWindowFocused = useIsWindowFocused();

  useInterval(async () => {
    if (isWindowFocused && (await Session.doesSessionExist())) {
      navigateToRegistration();
    }
  }, SESSION_POLL_INTERVAL_MS);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      if (await Session.doesSessionExist()) {
        if (!cancelled) {
          navigateToRegistration();
        }
      }
    }

    window.addEventListener("focus", checkSession);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", checkSession);
    };
  }, [navigateToRegistration]);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }

    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);

    return () => clearTimeout(timer);
  }, [cooldown]);

  const onResend = useCallback(async () => {
    if (!email || cooldown > 0) {
      return;
    }

    resendingHandlers.setTrue();
    setResendError(undefined);

    try {
      const response = await createCode({
        email,
        options: {
          preAPIHook: (x) => {
            const url = new URL(x.url);
            url.searchParams.set("languageId", i18n.language);
            if (appBranding !== DEFAULT_APP_BRANDING) {
              url.searchParams.set("brand", appBranding);
            }

            return Promise.resolve({
              ...x,
              url: url.toString(),
              requestInit: removeFromHeaders(x.requestInit, "Authorization"),
            });
          },
        },
      });

      if (response.status === "SIGN_IN_UP_NOT_ALLOWED") {
        setResendError(response.reason);
      } else {
        setCooldown(RESEND_COOLDOWN_SECONDS);
        showFlashToast({ variant: "success", message: t("page.magic-link.sent.resend-toast") });
      }
    } catch (err: any) {
      if (err.isSuperTokensGeneralError === true) {
        setResendError(err.message);
      } else {
        showFlashToast({ variant: "alert", message: t("common.generic-error") });
      }
    } finally {
      resendingHandlers.setFalse();
    }
  }, [email, cooldown, i18n.language, appBranding, resendingHandlers, showFlashToast, t]);

  return (
    <AuthenticationPage header={t("page.magic-link.sent.title")}>
      <Stack gap="lg">
        <p>
          <Trans
            i18nKey="page.magic-link.sent.description"
            values={{ email: email || "" }}
            components={{
              email: <span className="text-text-brand-secondary" />,
            }}
          />
        </p>

        <aside className="pt-page-bottom">
          <Stack gap="sm">
            <Inline gap="xs" align="start">
              <span className="mt-0.5 size-icon-inline shrink-0">
                <AlertCircleIcon size={16} tone="secondary" />
              </span>
              <span className="text-caption text-text-secondary">{t("page.magic-link.sent.no-link")}</span>
            </Inline>
            <Inline gap="xs" align="center">
              <span className="size-icon-inline shrink-0">
                <Hourglass01Icon size={16} tone="secondary" />
              </span>
              <span className="text-caption text-text-secondary">{t("page.magic-link.sent.expiry")}</span>
            </Inline>
            {resendError && <p className="text-caption text-text-danger-secondary">{resendError}</p>}
          </Stack>
        </aside>

        <Stack gap="sm" align="center">
          <Button
            variant="soft"
            type="link"
            href={`${routes.authentication.magicLinkEnterCode()}?email=${encodeURIComponent(email || "")}`}
            fullWidth
          >
            {t("page.magic-link.sent.enter-code")}
          </Button>
          <Button variant="ghostPrimary" onClick={onResend} disabled={cooldown > 0} isLoading={isResending}>
            {cooldown > 0
              ? t("page.magic-link.sent.resend-cooldown", { seconds: cooldown })
              : t("page.magic-link.sent.resend")}
          </Button>
        </Stack>
      </Stack>
    </AuthenticationPage>
  );
}
